import { getBCP47Language } from '@portal/shared/utils/locale';
import { MoneyV2 } from 'gql/shopifyStorefront/graphql';
import { useCallback } from 'react';
import { ShopifyPrice } from 'types/shopify/common.types';
import { useLocale } from './useLocale';

type ShopifyPriceInput = ShopifyPrice | MoneyV2;

interface PriceFormatOptions {
  /**
   * Whether to hide decimal places for whole numbers
   * @default false
   */
  hideDecimalsForWholeNumbers?: boolean;
}

/**
 * Converts a string or number amount to the smallest currency unit (e.g., cents)
 * while preserving precision
 */
function toSmallestCurrencyUnit(amount: number | string): number {
  if (typeof amount === 'string') {
    // Handle string input by splitting on decimal point
    const [whole, fraction = ''] = amount.split('.');
    // Convert to smallest unit by multiplying whole part by 100 and adding fraction
    return parseInt(whole, 10) * 100 + parseInt(fraction.padEnd(2, '0'), 10);
  }
  // For number input, multiply by 100 and round to avoid floating point issues
  return Math.round(amount * 100);
}

/**
 * Hook for formatting money values using Intl.NumberFormat
 * Handles both regular money values and Shopify money objects
 * Respects locale-specific currency display conventions
 */
export function useMoney() {
  const locale = useLocale();

  const formatMoney = useCallback(
    /**
     * Formats a numeric amount into a localized currency string
     * @param amount - The amount to format (number or string)
     * @param currencyCode - The ISO 4217 currency code (e.g., 'USD', 'EUR')
     * @param options - Optional formatting options for the price display
     * @returns A formatted currency string according to the locale and options
     * @throws Error if currencyCode is not a valid ISO 4217 code
     */
    (amount: number | string, currencyCode: string, options: PriceFormatOptions = {}): string => {
      // Validate currency code format (ISO 4217)
      if (!/^[A-Z]{3}$/.test(currencyCode)) {
        throw new Error(
          `Invalid currency code: ${currencyCode}. Must be a valid ISO 4217 code (3 uppercase letters).`
        );
      }

      const { hideDecimalsForWholeNumbers = false } = options;

      // Convert to smallest currency unit for precise handling
      const amountInSmallestUnit = toSmallestCurrencyUnit(amount);
      // Convert back to the main currency unit for display
      const numericAmount = amountInSmallestUnit / 100;

      // Get the BCP47 language code for the current locale
      const bcp47Locale = getBCP47Language(locale);

      // Adjust fraction digits for whole numbers if requested
      const adjustedFractionDigits =
        hideDecimalsForWholeNumbers && Number.isInteger(numericAmount)
          ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          : {};

      // Create the formatter with the specified options
      const formatter = new Intl.NumberFormat(bcp47Locale, {
        style: 'currency',
        currency: currencyCode,
        ...adjustedFractionDigits
      });

      // Format the price
      return formatter.format(numericAmount);
    },
    [locale]
  );

  const formatShopifyMoney = useCallback(
    /**
     * Formats a Shopify price object into a localized currency string
     * @param price - The Shopify price object containing amount and currencyCode
     * @param options - Optional formatting options for the price display
     * @returns A formatted currency string according to the locale and options
     */
    (price: ShopifyPriceInput, options: PriceFormatOptions = {}): string => {
      return formatMoney(price.amount, price.currencyCode, options);
    },
    [formatMoney]
  );

  return { formatMoney, formatShopifyMoney };
}
